import React, { useContext } from 'react';
import { Body, SmallBody, RegularTitle } from '@indigo-ag/component-module';
import i18next from 'core/i18n';
import { AgFinancing, AgOpportunity } from '@indigo-ag/schema';

import { Card, utils, SalesforceLink, createStylesHook, Permissions, GridContainerItem, GridContainer } from 'shared';
import { brazil, parseNumberToLocale, salesforceUtils, text, UserUtil } from 'shared/utils';
import { UserContext } from 'core/components/GateWay/GateWay';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { getTranslatedCropName } from '../DecisionSheet/utils/decisionSheet.util';

interface FullOrderDetailProps {
  creditAnalystAssigned?: AgFinancing['creditAnalystAssigned'];
  orderInfo: AgFinancing['orderInfo'];
  salesforceId: string;
}

const useStyles = createStylesHook({
  card: {
    border: '1px solid #E0E0E0',
    borderRadius: 8,
    padding: 24,
    marginBottom: 16
  }
});

export const FullOrderDetail: React.FunctionComponent<FullOrderDetailProps> = ({
  creditAnalystAssigned,
  orderInfo,
  salesforceId
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * User Context
   */
  const { hasPermission } = useContext(UserContext);
  const { agFinance } = useContext(FinanceProcessContext);
  const phase2 = agFinance?.phase2;
  const isLuchesi = hasPermission(Permissions.FA_EXTERNAL_LEGAL);
  const isCreditLead = hasPermission(Permissions.FA_CREDIT_LEAD);
  const isInternal = hasPermission(Permissions.FA_INTERNAL) || hasPermission(Permissions.FA_ADMIN);

  const isCnpj = orderInfo?.cpfOrCnpj?.length === 14;

  const seeAllOpportunitiesLink = salesforceUtils.getAllOpportunities(salesforceId);
  const opportunities = orderInfo?.opportunities;

  const getOpportunityGridItems = (opportunity: AgOpportunity): GridContainerItem[] => {
    const items: GridContainerItem[] = [
      {
        label: i18next.t('contractValue'),
        value: opportunity.contractValue
          ? utils.currency.inputMaskCurrency(opportunity.contractValue, {
              useComaForDecimals: true
            })
          : undefined
      },
      {
        label: i18next.t('approvedLimit'),
        value: phase2?.approvedCreditAmount ? utils.currency.formatBrazilCurrency(phase2?.approvedCreditAmount) : '-'
      },
      {
        label: i18next.t('cvExpirationDate'),
        value: utils.date.format({ date: opportunity?.dueDate, useUTC: true })
      }
    ];

    if (opportunity.collateralDueDate && (isLuchesi || isCreditLead || isInternal)) {
      items.push({
        label: i18next.t('collateralDueDate'),
        value: utils.date.format({ date: opportunity.collateralDueDate, useUTC: true })
      });
    }

    items.push(
      ...[
        {
          label: i18next.t('warrantyAmount'),
          value: opportunity.collateralValue
            ? utils.currency.formatBrazilCurrency(opportunity.collateralValue)
            : undefined
        },
        {
          label: i18next.t('warrantyArea'),
          value: opportunity.collateralHectares
            ? `${parseNumberToLocale(opportunity.collateralHectares)} Ha`
            : undefined
        },
        {
          label: i18next.t('collateralBags'),
          value: parseNumberToLocale(Math.ceil(opportunity.collateralBags130 || 0))
        },
        {
          label: i18next.t('bagsPriceBase'),
          value: parseNumberToLocale(opportunity?.pricePerBag || 0)
        },
        {
          label: i18next.t('bagsBase'),
          value: parseNumberToLocale(opportunity?.bagsPerHectares || 0)
        },
        {
          label: i18next.t('warrantyType'),
          value: opportunity.collateralType
        },
        {
          label: i18next.t('numberOfRegistrations'),
          value: parseNumberToLocale(opportunity?.quantityOfMatriculas)
        },
        {
          label: i18next.t('type'),
          value: i18next.t(`opportunityType-${opportunity.type}`)
        },
        {
          label: i18next.t('products'),
          value: opportunity?.products ? opportunity.products.join(', ') : undefined
        },
        {
          label: i18next.t('plantedArea'),
          value: opportunity.contractHectares
            ? `${utils.numberWithDots(opportunity.contractHectares, 2)} Ha`
            : undefined
        },
        {
          label: i18next.t('commercialRepresentative'),
          value: opportunity.responsible
        },
        {
          label: i18next.t('tradingName'),
          value: opportunity.tradingName
        },
        {
          label: i18next.t('tradingAddress'),
          value: opportunity.tradingAddress
        },
        {
          label: text.toTitleCase(i18next.t('channelPartnerName')),
          value: opportunity.channelPartnerName
        },
        {
          label: text.toTitleCase(i18next.t('createdAt')),
          value: opportunity.createdDate ? utils.date.formatDate(opportunity.createdDate) : undefined
        },
        {
          label: text.toTitleCase(i18next.t('sfdcOpportunityId')),
          value: opportunity.id ?? undefined
        },
        {
          label: text.toTitleCase(i18next.t('sfdcAccountId')),
          value: orderInfo?.clientId ?? undefined
        },
        {
          label: text.toTitleCase(i18next.t('supplierPaymentDate')),
          value: opportunity.supplierPaymentDate
            ? utils.date.format({ date: opportunity.supplierPaymentDate, useUTC: true })
            : undefined
        }
      ]
    );

    return items;
  };

  return (
    <>
      <div className="flex">
        <div style={{ width: '532px' }}>
          <Card title={i18next.t('generalInformation')}>
            <>
              <SmallBody>{i18next.t('clientName')}</SmallBody>
              <RegularTitle>{orderInfo?.client || ''}</RegularTitle>
              <SmallBody>{isCnpj ? 'CNPJ' : 'CPF'}</SmallBody>
              <RegularTitle>
                {orderInfo?.cpfOrCnpj
                  ? isCnpj
                    ? brazil.cnpjInputMask(orderInfo?.cpfOrCnpj || '')
                    : brazil.cpfInputMask(orderInfo?.cpfOrCnpj || '')
                  : ''}
              </RegularTitle>
              <SmallBody>{i18next.t('state')}</SmallBody>
              <RegularTitle>{orderInfo?.state || ''}</RegularTitle>
              <SmallBody>{i18next.t('municipality')}</SmallBody>
              <RegularTitle>{orderInfo?.municipality || ''}</RegularTitle>
              <SmallBody>{i18next.t('campaign')}</SmallBody>
              <RegularTitle>{orderInfo?.campaign || ''}</RegularTitle>
              <SmallBody>{i18next.t('crop')}</SmallBody>
              <RegularTitle>{getTranslatedCropName(orderInfo?.sellingSeason || '')}</RegularTitle>
              <SmallBody>{i18next.t('maritalStatus')}</SmallBody>
              <RegularTitle>{orderInfo?.maritalStatus || ''}</RegularTitle>
              <SmallBody>{i18next.t('generalRegistry')}</SmallBody>
              <RegularTitle>{orderInfo?.rg}</RegularTitle>
              <SmallBody>{text.toTitleCase(i18next.t('creditAnalyst'))}</SmallBody>
              <RegularTitle>{UserUtil.getUserFullName(creditAnalystAssigned)}</RegularTitle>
            </>
          </Card>
        </div>
        <div style={{ marginLeft: 32, width: '90%' }}>
          {orderInfo?.opportunities && orderInfo?.opportunities.length ? (
            <Card
              title={i18next.t('opportunityDetails')}
              topRightContent={
                orderInfo?.opportunities.length > 1 && salesforceId ? (
                  <a target="_blank" href={seeAllOpportunitiesLink} rel="noopener noreferrer">
                    {i18next.t('seeAllOpportunities')}
                  </a>
                ) : (
                  undefined
                )
              }
            >
              <>
                {opportunities?.map((opportunity, i) => {
                  return (
                    <div className={classes.card} key={i + 'opportunity'}>
                      <GridContainer columns={4} items={getOpportunityGridItems(opportunity)} />
                      {opportunity?.id && <SalesforceLink id={opportunity?.id} />}
                    </div>
                  );
                })}
              </>
            </Card>
          ) : (
            <Body>{i18next.t('noResultsFound')}</Body>
          )}
        </div>
      </div>
    </>
  );
};
